/**
 * "Look what I have."
 *
 * The word collection ([Review](../../../docs/REVIEW.md#word-collection)), from
 * the `23-words` mockup. The path shows a child *where they are*; this is the
 * only screen that shows them *what they have*, which is the thing four
 * chapters of walking cannot point at.
 *
 * **Nothing here moves backwards.** A word that has decayed quietly comes back
 * for review, and its card stays where it got to
 * ([D-032](../../../docs/DECISIONS.md#d-032)). That rule lives in
 * `packages/core` and is applied by the server; this screen only draws what it
 * is given, so there is no second place for it to be got wrong.
 *
 * **Finding a word** ([D-136](../../../docs/DECISIONS.md#d-136)): a search
 * that reads English and Danish, the stage chips, and a dropdown each for book,
 * chapter and kind of word. A dropdown appears only when it has more than one
 * thing to choose between, so a child with one chapter of words is not offered
 * a choice of one.
 */

import '@capytalk/tokens/blocks/words.css';

import { STAGE_NAMES, type CollectionStage } from '@capytalk/core';
import { useEffect, useMemo, useState } from 'react';
import { type Client, type Collection, type Trouble, troubleFrom } from './api.js';
import { assetUrl, say } from './audio.js';
import Sneakers from './Sneakers.js';
import {
  KIND_NAMES,
  type Kind,
  NO_FILTER,
  type WordFilter,
  choices,
  narrowed,
  passes,
} from './wordFilter.js';

type Word = Collection['words'][number];

/**
 * How a stage is drawn.
 *
 * Growing things, not grades: a seedling is not a bad mark and a tree is not an
 * A. Nothing on this screen tells a child that a word is *behind*.
 */
const MARKS: Readonly<Record<CollectionStage, string>> = {
  0: '🌱',
  1: '🌿',
  2: '🌳',
  3: '⭐',
};

/** The chips, in order. `null` is "All". */
const FILTERS: readonly (CollectionStage | null)[] = [null, 0, 1, 2, 3];

const chipLabel = (stage: CollectionStage | null): string =>
  stage === null ? 'All' : `${MARKS[stage]} ${STAGE_NAMES[stage]}`;

/** One card in the grid. */
function Card({ word, onOpen }: { readonly word: Word; readonly onOpen: () => void }) {
  return (
    <button
      type="button"
      className={word.stage === 0 ? 'word new' : 'word'}
      // A card is a control, and `.word` is a mockup div — so the button's own
      // chrome is removed rather than the block being changed, which would move
      // every other screen that uses it.
      style={{ font: 'inherit', color: 'inherit', cursor: 'pointer' }}
      // What a screen reader says instead of "🏠 house 🌳", which is a picture,
      // a word and an emoji read as three unrelated things.
      aria-label={`${word.word}, ${STAGE_NAMES[word.stage]}`}
      onClick={onOpen}
    >
      <div className="a" aria-hidden="true">
        {word.imageRef === null ? (
          '📖'
        ) : (
          <img
            src={assetUrl(word.imageRef)}
            alt=""
            style={{ width: 30, height: 30, display: 'block', margin: '0 auto' }}
          />
        )}
      </div>
      <div className="w" aria-hidden="true">
        {word.word}
      </div>
      <div className="s" aria-hidden="true">
        {MARKS[word.stage]}
      </div>
    </button>
  );
}

/**
 * One word, opened.
 *
 * Hear it, what it means, and which chapter taught it — the last is the part
 * that turns a grid into a memory: *"that one's from Hello!"*.
 */
function Detail({
  word,
  onClose,
}: {
  readonly word: Word;
  readonly onClose: () => void;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', key);
    return () => {
      globalThis.removeEventListener('keydown', key);
    };
  }, [onClose]);

  return (
    <>
      <button
        className="dim"
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{ border: 0, padding: 0 }}
        onClick={onClose}
      />
      <div className="pop" role="dialog" aria-modal="true" aria-label={word.word}>
        <div className="between">
          <h2>{word.word}</h2>
          <button
            className="x"
            type="button"
            aria-label="Close"
            style={{ background: 'none', border: 0, font: 'inherit' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="row" style={{ gap: 12, marginTop: 10 }}>
          {word.imageRef !== null && (
            <img
              src={assetUrl(word.imageRef)}
              alt=""
              style={{ width: 72, borderRadius: 12, display: 'block' }}
            />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>{word.meaning}</div>
            <div className="tiny" style={{ marginTop: 4 }}>
              {MARKS[word.stage]} {STAGE_NAMES[word.stage]}
              {word.chapter !== null && ` · from ${word.chapter}`}
            </div>
          </div>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="chip"
            type="button"
            onClick={() => {
              void say(word.audioRef, word.word);
            }}
          >
            <span aria-hidden="true">🔊</span> Hear it
          </button>
          <button
            className="chip"
            type="button"
            onClick={() => {
              void say(word.audioRef, word.word, { slow: true });
            }}
          >
            <span aria-hidden="true">🐢</span> Slower
          </button>
        </div>
      </div>
    </>
  );
}

/** The search box and the dropdowns. Each dropdown only when it has a choice. */
function Finder({
  words,
  filter,
  set,
}: {
  readonly words: readonly Word[];
  readonly filter: WordFilter;
  readonly set: (change: Partial<WordFilter>) => void;
}) {
  const offered = useMemo(() => choices(words, filter.book), [words, filter.book]);
  const select = {
    minHeight: 44,
    borderRadius: 12,
    padding: '6px 10px',
    font: 'inherit',
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <input
        type="search"
        aria-label="Find a word"
        placeholder="Find a word…"
        value={filter.query}
        style={{ ...select, width: '100%', border: '1.5px solid var(--line)' }}
        onChange={(event) => {
          set({ query: event.target.value });
        }}
      />
      <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        {offered.books.length > 1 && (
          <select
            aria-label="Book"
            value={filter.book ?? ''}
            style={select}
            onChange={(event) => {
              const value = event.target.value;
              // A new book clears the chapter, which may not be in it.
              set({ book: value === '' ? null : Number(value), chapter: null });
            }}
          >
            <option value="">All books</option>
            {offered.books.map((book) => (
              <option key={book} value={book}>
                Book {book}
              </option>
            ))}
          </select>
        )}
        {offered.chapters.length > 1 && (
          <select
            aria-label="Chapter"
            value={filter.chapter ?? ''}
            style={select}
            onChange={(event) => {
              set({ chapter: event.target.value === '' ? null : event.target.value });
            }}
          >
            <option value="">All chapters</option>
            {offered.chapters.map((chapter) => (
              <option key={chapter} value={chapter}>
                {chapter}
              </option>
            ))}
          </select>
        )}
        {offered.kinds.length > 1 && (
          <select
            aria-label="Kind of word"
            value={filter.kind ?? ''}
            style={select}
            onChange={(event) => {
              const value = event.target.value;
              set({ kind: value === '' ? null : (value as Kind) });
            }}
          >
            <option value="">All kinds of word</option>
            {offered.kinds.map((kind) => (
              <option key={kind} value={kind}>
                {KIND_NAMES[kind]}
              </option>
            ))}
          </select>
        )}
        {narrowed(filter) && (
          <button
            type="button"
            className="chip"
            onClick={() => {
              set(NO_FILTER);
            }}
          >
            Show all
          </button>
        )}
      </div>
    </div>
  );
}

export default function Words({
  client,
  preloaded = null,
}: {
  readonly client: Client;
  /** The collection, when whoever shows this screen already fetched it. */
  readonly preloaded?: Collection | null;
}) {
  const [collection, setCollection] = useState<Collection | null>(preloaded);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [filter, setFilter] = useState<WordFilter>(NO_FILTER);
  const set = (change: Partial<WordFilter>) => {
    setFilter((was) => ({ ...was, ...change }));
  };
  const [open, setOpen] = useState<Word | null>(null);

  useEffect(() => {
    if (preloaded !== null) return;
    let live = true;
    // Through `Promise.resolve` because a server older than this app has no
    // `collection` procedure and the call *throws* rather than rejecting
    // (D-066) — which would take the tab down instead of showing the trouble.
    Promise.resolve()
      .then(() => client.learner.collection.query())
      .then((next) => {
        if (live) setCollection(next);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client, preloaded]);

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : 'Your words would not load.'}
        </p>
      </div>
    );
  }

  if (collection === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Counting your words…</p>
      </div>
    );
  }

  // Before the first lesson there is nothing to filter, nothing to count and
  // nothing to be proud of yet — so the screen says what happens next instead
  // of showing an empty grid under a zero.
  if (collection.total === 0) {
    return (
      <div className="grow pad center">
        <Sneakers text="Let's start filling this up!" audioRef={null} />
        <p className="sub">Every word you meet in a lesson lands here.</p>
      </div>
    );
  }

  const shown = collection.words.filter((word) => passes(word, filter));

  return (
    <div className="grow pad" style={{ overflowY: 'auto' }}>
      {/* Possession, always: "you know", never "you have learned 12 of 400". */}
      <h2>
        You know {collection.total} {collection.total === 1 ? 'word' : 'words'}
      </h2>

      <div
        className="row"
        style={{ gap: 7, marginTop: 12, marginBottom: 12, flexWrap: 'wrap' }}
        role="group"
        aria-label="Show which words"
      >
        {FILTERS.map((stage) => (
          <button
            key={stage ?? 'all'}
            className={filter.stage === stage ? 'chip on' : 'chip'}
            type="button"
            aria-pressed={filter.stage === stage}
            onClick={() => {
              set({ stage });
            }}
          >
            {chipLabel(stage)}
          </button>
        ))}
      </div>

      <Finder words={collection.words} filter={filter} set={set} />

      {shown.length === 0 ? (
        // A filter that matches nothing, which is not the same as having no
        // words — and saying "you know 0 words" here would be a lie.
        <p className="sub">
          {narrowed({ ...filter, stage: null })
            ? 'No words match that. Try another search.'
            : 'No words at that stage yet. Keep going!'}
        </p>
      ) : (
        <div className="grid3">
          {shown.map((word) => (
            <Card
              key={word.lexemeId}
              word={word}
              onOpen={() => {
                setOpen(word);
              }}
            />
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="tiny" style={{ fontWeight: 700 }}>
          🌱 new · 🌿 learning · 🌳 known · ⭐ strong
          <br />
          Words never go backwards here — once you know one, it stays.
        </div>
      </div>

      {open !== null && (
        <Detail
          word={open}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
